import { LitElement, css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js';
import type { SlInput } from '@shoelace-style/shoelace'
import { generateUUID } from '../functions/generateUUID';


@customElement('ep-chip-input')
export class EPChipInput extends LitElement {
  @property({ attribute: 'type', reflect: true })
  type: SlInput['type'] = 'text';

  @property({ attribute: 'placeholder' })
  placeholder: string = ''
  
  @property({ type: Boolean, attribute: 'disabled', reflect: true })
  disabled: boolean = false

  @state()
  innerValues: { id: string, value: string}[] = [];

  get values(): string[] {
    return this.innerValues.map(v => v.value);
  }
  set values(vals: string[]) {
    this.innerValues = vals.map(v => ({ id: generateUUID(), value: v }));
  }

  @property({ attribute: false })
  draftedValue: string = '';

  private _handleAddDraftedValue() {
    if (this.draftedValue.length > 0) {
      this.innerValues = [...this.innerValues, { id: generateUUID(), value: this.draftedValue }]
      //this.values = [...this.values, this.draftedValue];
      this.draftedValue = '';
      // Dispatch event
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: this.values,
      }))
    }
  }

  private _handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this._handleAddDraftedValue()
    }
  }

  private _handleRemoveClicked(id: string) {
    this.innerValues = [...this.innerValues.filter(v => v.id !== id)];
    // Dispatch event
    this.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      detail: this.values,
    }))
  }

  // firstUpdated(): void {
  //   this.values = ['test', 'Hello']
  // }

  render() {
    return html`
      <sl-input .type=${this.type} ?disabled=${this.disabled} .placeholder=${this.placeholder}
        .value=${this.draftedValue}
        @sl-input=${(event: InputEvent) => this.draftedValue = (event.target as HTMLInputElement).value}
        @keydown=${this._handleKeyDown}
        >
        <div class="chips" slot="prefix">
          ${this.innerValues.map(({ id, value }) => html`
            <ep-chip>
              ${value}
              <sl-icon-button slot="trailing-icon" name="x-circle" label="Remove" @click=${() => this._handleRemoveClicked(id)}></sl-icon-button>
            </ep-chip>
          `)}
        </div>
        <sl-tooltip slot="suffix" hoist content="Press Enter to add to the list">
          <sl-icon-button class="add-button" name="plus-square-fill" label="Add" 
            style=${styleMap({
              opacity: this.draftedValue.length > 0 ? '1.0' : '0.0'
            })}
            @click=${this._handleAddDraftedValue}
            >
          </sl-icon-button>
        </sl-tooltip>
        <slot slot="label" name="label"></slot>
        <slot slot="help-text" name="help-text"></slot>
      </sl-input>
    `
  }

  static styles = css`
    :host {
      display: contents;
      --chip-height: 28px;
    }
    .chips {
      display: inline-flex;
      gap: 5px;
      margin-inline-start: 8px;
    }
    ep-chip > sl-icon-button {
      --temp-font-size: 0.9em;
      font-size: var(--temp-font-size);
      width: var(--temp-font-size);
      height: var(--temp-font-size);

      margin-left: -4px;
    }
    ep-chip > sl-icon-button::part(base) {
      padding: 0;
    }

    .add-button {
      color: green;
      transition: 250ms opacity;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-chip-input': EPChipInput
  }
}
